// pages/RecordingStudio.jsx
import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Play,
  Pause,
  Square,
  SkipBack,
  SkipForward,
  Plus,
  Save,
  Download,
  Music,
  Settings,
  Volume2,
  VolumeX,
  Repeat,
  ChevronLeft,
  ChevronRight,
  Loader,
  AlertCircle,
  Layers,
  FolderOpen,
  Zap,
  Mic2,
} from "lucide-react";
import VoiceSelector from "../../components/RecordingStudio/VoiceSelector";
import MusicLibrary from "../../components/RecordingStudio/MusicLibrary";
import ProjectSettings from "../../components/RecordingStudio/ProjectSettings";
import VoiceBlock from "../../components/RecordingStudio/VoiceBlock";
import Timeline from "../../components/RecordingStudio/Timeline";
import BlockEditor from "../../components/RecordingStudio/BlockEditor";
import MusicTrimModal from "../../components/RecordingStudio/MusicTrimModal";
import ProjectsDrawer from "../../components/RecordingStudio/ProjectsDrawer";
import api from "../../services/api";

// ─── Local draft persistence helpers ─────────────────────────────────────────
const getDraftKey = (projectId) => `recordingStudioDraft:${projectId}`;

const readDraft = (projectId) => {
  if (!projectId) return null;
  try {
    const raw = localStorage.getItem(getDraftKey(projectId));
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const writeDraft = (projectId, blocks, backgroundMusic) => {
  if (!projectId) return;
  const payload = {
    blocks: (blocks || []).map((b) => ({
      id: b.id,
      timelineStart: b.timelineStart ?? 0,
      duration: b.duration ?? null,
      orderIndex: b.orderIndex ?? 0,
    })),
    backgroundMusic: backgroundMusic
      ? {
          trimStart: backgroundMusic.trimStart ?? 0,
          trimEnd: backgroundMusic.trimEnd ?? null,
          volume: backgroundMusic.volume ?? 0.3,
        }
      : null,
    ts: Date.now(),
  };
  localStorage.setItem(getDraftKey(projectId), JSON.stringify(payload));
};

const mergeBlocksWithDraft = (serverBlocks, draft) => {
  if (!draft?.blocks?.length) return serverBlocks;
  const dMap = new Map(draft.blocks.map((b) => [b.id, b]));
  const merged = serverBlocks.map((b) => {
    const d = dMap.get(b.id);
    if (!d) return b;
    return {
      ...b,
      timelineStart: d.timelineStart,
      duration: d.duration ?? b.duration,
      orderIndex: d.orderIndex ?? b.orderIndex,
    };
  });

  return [...merged].sort((a, b) => (a.orderIndex ?? 0) - (b.orderIndex ?? 0));
};

const mergeMusicWithDraft = (project, draft) => {
  if (!project?.backgroundMusic || !draft?.backgroundMusic) return project;
  return {
    ...project,
    backgroundMusic: {
      ...project.backgroundMusic,
      trimStart: draft.backgroundMusic.trimStart ?? 0,
      trimEnd: draft.backgroundMusic.trimEnd ?? null,
      volume: draft.backgroundMusic.volume ?? 0.3,
    },
  };
};

// ─── Sidebar drag wrapper ──────────────────────────────────────────────────
const SidebarBlock = ({
  block,
  index,
  isSelected,
  onSelect,
  onSynthesize,
  onUpdate,
  onDelete,
  onDragStart,
  onDragOver,
  onDrop,
  isDraggingOver,
  avatarImageUrl,
}) => (
  <div
    draggable
    onDragStart={() => onDragStart(index)}
    onDragOver={(e) => {
      e.preventDefault();
      onDragOver(index);
    }}
    onDrop={() => onDrop(index)}
    className={`relative transition-all duration-150 ${isDraggingOver ? "scale-[1.02] opacity-60" : ""}`}
  >
    <VoiceBlock
      block={block}
      index={index}
      isSelected={isSelected}
      onSelect={onSelect}
      onSynthesize={onSynthesize}
      onUpdate={onUpdate}
      onDelete={onDelete}
      avatarImageUrl={avatarImageUrl}
    />
  </div>
);

// ─── Web Audio Playback Engine ─────────────────────────────────────────────
class PlaybackEngine {
  constructor() {
    this._ctx = null;
    this._sources = [];
    this._rafId = null;
    this._mode = "idle";
    this._renderedAudio = null;
    this._contextStartTime = 0;
    this._seekOffset = 0;
    this.volume = 1;
    this.muted = false;
    this.onTimeUpdate = null;
    this.onEnded = null;
    this.totalDuration = 0;
  }

  _getCtx() {
    if (!this._ctx || this._ctx.state === "closed") {
      this._ctx = new (window.AudioContext || window.webkitAudioContext)();
    }
    return this._ctx;
  }

  _stopAll() {
    if (this._rafId) {
      cancelAnimationFrame(this._rafId);
      this._rafId = null;
    }
    this._sources.forEach(({ source, gainNode }) => {
      try {
        source.stop();
      } catch (_) {}
      try {
        gainNode.disconnect();
      } catch (_) {}
    });
    this._sources = [];
    if (this._renderedAudio) {
      try {
        this._renderedAudio.pause();
      } catch (_) {}
      this._renderedAudio = null;
    }
    this._mode = "idle";
  }

  _currentPlayTime() {
    if (!this._ctx) return this._seekOffset;
    return this._seekOffset + (this._ctx.currentTime - this._contextStartTime);
  }

  _startRAF(loop, loopFn) {
    const tick = () => {
      if (this._mode === "idle") return;
      const t = this._currentPlayTime();
      this.onTimeUpdate?.(Math.max(0, t));
      if (t >= this.totalDuration) {
        if (loop) {
          loopFn();
        } else {
          this._stopAll();
          this.onEnded?.();
        }
        return;
      }
      this._rafId = requestAnimationFrame(tick);
    };
    this._rafId = requestAnimationFrame(tick);
  }

  async _fetchBuffer(url) {
    const ctx = this._getCtx();
    const resp = await fetch(url);
    const arrayBuffer = await resp.arrayBuffer();
    return ctx.decodeAudioData(arrayBuffer);
  }

  static buildSchedule(blocks) {
    return [...blocks]
      .filter((b) => b.status === "completed" && b.audioUrl)
      .map((b, i, arr) => {
        const start =
          b.timelineStart != null
            ? parseFloat(b.timelineStart)
            : arr.slice(0, i).reduce((acc, prev) => {
                return acc + (prev.duration ? prev.duration / 1000 : 5);
              }, 0);
        const dur = b.duration ? b.duration / 1000 : 5;
        return { block: b, start, end: start + dur };
      })
      .sort((a, b) => a.start - b.start);
  }

  async play(
    blocks,
    fromTime = 0,
    renderedUrl = null,
    loop = false,
    bgMusicUrl = null,
    bgTrimStart = 0,
    bgTrimEnd = null,
  ) {
    this._stopAll();

    if (renderedUrl) {
      this._mode = "rendered";
      const audio = new Audio(renderedUrl);
      audio.volume = this.muted ? 0 : this.volume;
      audio.currentTime = fromTime;
      this._renderedAudio = audio;
      this._seekOffset = fromTime;
      const ctx = this._getCtx();
      if (ctx.state === "suspended") await ctx.resume();
      this._contextStartTime = ctx.currentTime;
      audio.addEventListener("timeupdate", () => {
        this.onTimeUpdate?.(audio.currentTime);
      });
      audio.addEventListener("ended", () => {
        if (loop) {
          audio.currentTime = 0;
          audio.play().catch(() => {});
        } else {
          this._stopAll();
          this.onEnded?.();
        }
      });
      audio.play().catch((e) => console.warn("Rendered audio play failed:", e));
      return;
    }

    const schedule = PlaybackEngine.buildSchedule(blocks);
    if (!schedule.length) {
      this.onEnded?.();
      return;
    }

    this.totalDuration = schedule[schedule.length - 1].end;
    this._mode = "sequential";
    this._seekOffset = fromTime;

    const ctx = this._getCtx();
    if (ctx.state === "suspended") await ctx.resume();
    this._contextStartTime = ctx.currentTime;

    const [buffers, bgBuffer] = await Promise.all([
      Promise.all(
        schedule.map(({ block }) =>
          this._fetchBuffer(block.audioUrl).catch((e) => {
            console.warn(`Failed to fetch audio for block ${block.id}:`, e);
            return null;
          }),
        ),
      ),
      bgMusicUrl
        ? this._fetchBuffer(bgMusicUrl).catch(() => null)
        : Promise.resolve(null),
    ]);

    if (this._mode !== "sequential") return;
    this._contextStartTime = ctx.currentTime;

    schedule.forEach(({ start, end }, i) => {
      const buffer = buffers[i];
      if (!buffer) return;
      if (end <= fromTime) return;
      const source = ctx.createBufferSource();
      source.buffer = buffer;
      source.loop = false;
      const gainNode = ctx.createGain();
      gainNode.gain.value = this.muted ? 0 : this.volume;
      source.connect(gainNode);
      gainNode.connect(ctx.destination);
      const delayFromNow = Math.max(0, start - fromTime);
      const ctxStartTime = this._contextStartTime + delayFromNow;
      const bufferOffset = fromTime > start ? fromTime - start : 0;
      const remainingDuration = end - start - bufferOffset;
      source.start(ctxStartTime, bufferOffset, remainingDuration);
      this._sources.push({ source, gainNode });
    });

    if (bgBuffer) {
      const trimStart = Math.max(0, bgTrimStart || 0);
      const trimEnd =
        bgTrimEnd && bgTrimEnd > trimStart
          ? Math.min(bgTrimEnd, bgBuffer.duration)
          : bgBuffer.duration;
      const trimmedDur = trimEnd - trimStart;
      const bgGain = ctx.createGain();
      bgGain.gain.value = this.muted ? 0 : Math.min(this.volume * 0.2, 0.2);
      bgGain.connect(ctx.destination);
      const bgSource = ctx.createBufferSource();
      bgSource.buffer = bgBuffer;
      bgSource.loop = true;
      bgSource.loopStart = trimStart;
      bgSource.loopEnd = trimEnd;
      bgSource.connect(bgGain);
      const regionOffset =
        trimmedDur > 0 ? trimStart + (fromTime % trimmedDur || 0) : trimStart;
      bgSource.start(this._contextStartTime, regionOffset);
      this._sources.push({ source: bgSource, gainNode: bgGain });
    }

    const loopFn = () =>
      this.play(blocks, 0, null, loop, bgMusicUrl, bgTrimStart, bgTrimEnd);
    this._startRAF(loop, loopFn);
  }

  pause() {
    if (this._mode === "idle") return;
    const pausedAt = this._currentPlayTime();
    if (this._rafId) {
      cancelAnimationFrame(this._rafId);
      this._rafId = null;
    }
    if (this._mode === "rendered" && this._renderedAudio)
      this._renderedAudio.pause();
    if (this._mode === "sequential") {
      this._sources.forEach(({ source }) => {
        try {
          source.stop();
        } catch (_) {}
      });
      this._sources = [];
      this._ctx?.suspend().catch(() => {});
    }
    this._mode = "paused";
    this._seekOffset = Math.max(0, pausedAt);
    return this._seekOffset;
  }

  stop() {
    this._stopAll();
    this._seekOffset = 0;
    this.onTimeUpdate?.(0);
  }

  setVolume(v) {
    this.volume = v;
    this._sources.forEach(({ gainNode }) => {
      gainNode.gain.value = this.muted ? 0 : v;
    });
    if (this._renderedAudio) this._renderedAudio.volume = this.muted ? 0 : v;
  }

  setMuted(m) {
    this.muted = m;
    this._sources.forEach(({ gainNode }) => {
      gainNode.gain.value = m ? 0 : this.volume;
    });
    if (this._renderedAudio) this._renderedAudio.volume = m ? 0 : this.volume;
  }

  destroy() {
    this._stopAll();
    this._ctx?.close().catch(() => {});
    this._ctx = null;
  }
}

// ─── Icon button ────────────────────────────────────────────────────────────
const IconBtn = ({
  onClick,
  active,
  disabled,
  title,
  children,
  variant = "ghost",
}) => {
  const base =
    "relative flex items-center justify-center rounded-xl transition-all duration-150 disabled:opacity-30 disabled:cursor-not-allowed";
  const variants = {
    ghost: `w-8 h-8 text-gray-600 hover:text-white ${active ? "text-violet-400" : ""}`,
    solid: "w-9 h-9 text-white shadow-lg",
  };
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={`${base} ${variants[variant]}`}
      style={
        variant === "solid"
          ? {
              background: "linear-gradient(135deg, #7c3aed, #6d28d9)",
              boxShadow: "0 4px 16px rgba(124,58,237,0.35)",
            }
          : active
            ? { background: "rgba(139,92,246,0.15)" }
            : undefined
      }
    >
      {children}
    </button>
  );
};

// ─── Main Component ────────────────────────────────────────────────────────
const RecordingStudio = () => {
  const [project, setProject] = useState(null);
  const [blocks, setBlocks] = useState([]);
  const [selectedBlock, setSelectedBlock] = useState(null);
  const [showVoiceSelector, setShowVoiceSelector] = useState(false);
  const [showMusicLibrary, setShowMusicLibrary] = useState(false);
  const [showProjectSettings, setShowProjectSettings] = useState(false);
  const [showTrimModal, setShowTrimModal] = useState(false);
  const [showProjectsDrawer, setShowProjectsDrawer] = useState(false);
  const [supportedFormats, setSupportedFormats] = useState([]);
  const [avatarMap, setAvatarMap] = useState({});

  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [masterVolume, setMasterVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [isLooping, setIsLooping] = useState(false);
  const [isRendering, setIsRendering] = useState(false);
  const [renderError, setRenderError] = useState(null);
  const [playRendered, setPlayRendered] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [editorCollapsed, setEditorCollapsed] = useState(false);
  const [dragSrcIndex, setDragSrcIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);

  const engineRef = useRef(null);
  const currentTimeRef = useRef(0);
  const isPlayingRef = useRef(false);
  const blocksRef = useRef(blocks);
  const projectRef = useRef(project);
  const loopRef = useRef(isLooping);
  const masterVolumeRef = useRef(masterVolume);
  const isMutedRef = useRef(isMuted);

  useEffect(() => {
    blocksRef.current = blocks;
  }, [blocks]);
  useEffect(() => {
    projectRef.current = project;
  }, [project]);
  useEffect(() => {
    loopRef.current = isLooping;
  }, [isLooping]);
  useEffect(() => {
    masterVolumeRef.current = masterVolume;
  }, [masterVolume]);
  useEffect(() => {
    isMutedRef.current = isMuted;
  }, [isMuted]);

  // Persist local draft continuously for refresh safety
  useEffect(() => {
    if (!project?.id) return;
    writeDraft(project.id, blocks, project.backgroundMusic || null);
  }, [
    project?.id,
    blocks,
    project?.backgroundMusic?.trimStart,
    project?.backgroundMusic?.trimEnd,
    project?.backgroundMusic?.volume,
  ]);

  useEffect(() => {
    const engine = new PlaybackEngine();
    engine.onTimeUpdate = (t) => {
      setCurrentTime(t);
      currentTimeRef.current = t;
    };
    engine.onEnded = () => {
      setIsPlaying(false);
      isPlayingRef.current = false;
      setCurrentTime(0);
      currentTimeRef.current = 0;
    };
    engineRef.current = engine;
    return () => engine.destroy();
  }, []);

  useEffect(() => {
    engineRef.current?.setVolume(masterVolume);
  }, [masterVolume]);
  useEffect(() => {
    engineRef.current?.setMuted(isMuted);
  }, [isMuted]);

  useEffect(() => {
    loadProject();
    loadSupportedFormats();
    loadAvatarMap();
  }, []);

  const loadAvatarMap = async () => {
    try {
      // Fetch across the languages we support so every block's voice resolves
      const langs = ["en-GB", "en-US", "es-ES", "fr-FR", "de-DE"];
      const responses = await Promise.allSettled(
        langs.map((lc) => api.getVoices(lc, { limit: 200 })),
      );
      const map = {};
      responses.forEach((r) => {
        if (r.status === "fulfilled") {
          (r.value.avatars || []).forEach((a) => {
            if (a.voiceId && a.avatarImageUrl)
              map[a.voiceId] = a.avatarImageUrl;
          });
        }
      });
      setAvatarMap(map);
    } catch (e) {
      console.warn("Failed to load avatar map:", e);
    }
  };

  useEffect(() => {
    if (selectedBlock) {
      const updated = blocks.find((b) => b.id === selectedBlock.id);
      if (updated) setSelectedBlock(updated);
    }
  }, [blocks]);

  useEffect(() => {
    const handleKey = (e) => {
      if (["INPUT", "TEXTAREA", "SELECT"].includes(e.target.tagName)) return;
      switch (e.code) {
        case "Space":
          e.preventDefault();
          togglePlay();
          break;
        case "Home":
        case "KeyR":
          e.preventDefault();
          handleSeek(0);
          break;
        case "ArrowLeft":
          e.preventDefault();
          handleSeek(
            Math.max(0, currentTimeRef.current - (e.shiftKey ? 5 : 1)),
          );
          break;
        case "ArrowRight":
          e.preventDefault();
          handleSeek(currentTimeRef.current + (e.shiftKey ? 5 : 1));
          break;
        case "KeyM":
          e.preventDefault();
          setIsMuted((m) => !m);
          break;
        case "KeyL":
          e.preventDefault();
          setIsLooping((l) => !l);
          break;
        case "Backspace":
        case "Delete":
          if (selectedBlock) handleDeleteBlock(selectedBlock.id);
          break;
        default:
          break;
      }
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [selectedBlock]);

  const completedBlocks = blocks.filter(
    (b) => b.status === "completed" && b.audioUrl,
  );
  const canPlay =
    completedBlocks.length > 0 || blocks.some((b) => b.text?.trim());

  const totalDuration = Math.max(
    (() => {
      if (!blocks.length) return project?.duration || 0;
      const schedule = PlaybackEngine.buildSchedule(blocks);
      return schedule.length ? schedule[schedule.length - 1].end : 0;
    })(),
    1,
  );

  const startPlayback = useCallback(
    async (fromTime = 0) => {
      const eng = engineRef.current;
      if (!eng) return;
      eng.setVolume(masterVolumeRef.current);
      eng.setMuted(isMutedRef.current);
      eng.totalDuration = totalDuration;
      const proj = projectRef.current;
      const renderedUrl = playRendered && proj?.audioUrl ? proj.audioUrl : null;
      const bgMusicUrl =
        !renderedUrl && proj?.backgroundMusic?.audioUrl
          ? proj.backgroundMusic.audioUrl
          : null;
      const bgMusicTrimStart = proj?.backgroundMusic?.trimStart ?? 0;
      const bgMusicTrimEnd = proj?.backgroundMusic?.trimEnd ?? null;
      try {
        await eng.play(
          blocksRef.current,
          fromTime,
          renderedUrl,
          loopRef.current,
          bgMusicUrl,
          bgMusicTrimStart,
          bgMusicTrimEnd,
        );
      } catch (e) {
        console.error("Playback failed:", e);
        setIsPlaying(false);
        isPlayingRef.current = false;
      }
    },
    [totalDuration, playRendered],
  );

  // Synthesize every block that hasn't been synthesized yet, then play.
  const handleSynthesizeAllAndPlay = useCallback(async () => {
    if (!project) return;
    const needsSynth = blocksRef.current.filter(
      (b) =>
        b.status !== "completed" && b.status !== "processing" && b.text?.trim(),
    );

    if (needsSynth.length > 0) {
      // Mark them all as processing immediately so the UI responds
      setBlocks((prev) =>
        prev.map((b) =>
          needsSynth.find((n) => n.id === b.id)
            ? { ...b, status: "processing" }
            : b,
        ),
      );

      // Synthesize in parallel
      const results = await Promise.allSettled(
        needsSynth.map((b) => api.synthesizeVoiceBlock(project.id, b.id)),
      );

      // Build merged block list synchronously — React setState is async so
      // blocksRef wouldn't reflect new audioUrls in time for startPlayback.
      const patchMap = {};
      results.forEach((result, i) => {
        const blockId = needsSynth[i].id;
        patchMap[blockId] =
          result.status === "fulfilled"
            ? result.value.block
            : { status: "failed" };
      });

      const mergedBlocks = blocksRef.current.map((b) =>
        patchMap[b.id] ? { ...b, ...patchMap[b.id] } : b,
      );

      // Flush into React state AND the ref together before playback reads it
      setBlocks(mergedBlocks);
      blocksRef.current = mergedBlocks;
      setPlayRendered(false);
    }

    // Now blocksRef.current has fresh audioUrls — safe to play
    setIsPlaying(true);
    isPlayingRef.current = true;
    startPlayback(currentTimeRef.current);
  }, [project, startPlayback]);

  const togglePlay = useCallback(() => {
    if (isPlayingRef.current) {
      const pausedAt = engineRef.current?.pause() ?? currentTimeRef.current;
      setCurrentTime(pausedAt);
      currentTimeRef.current = pausedAt;
      setIsPlaying(false);
      isPlayingRef.current = false;
      return;
    }
    // If there are blocks that need synthesizing, do that first then play.
    const hasPending = blocksRef.current.some(
      (b) =>
        b.status !== "completed" && b.status !== "processing" && b.text?.trim(),
    );
    if (hasPending) {
      handleSynthesizeAllAndPlay();
    } else if (canPlay) {
      setIsPlaying(true);
      isPlayingRef.current = true;
      startPlayback(currentTimeRef.current);
    }
  }, [canPlay, startPlayback, handleSynthesizeAllAndPlay]);

  const handleSeek = useCallback(
    (time) => {
      const t = Math.max(0, time);
      setCurrentTime(t);
      currentTimeRef.current = t;
      if (isPlayingRef.current) startPlayback(t);
    },
    [startPlayback],
  );

  const handleStop = useCallback(() => {
    setIsPlaying(false);
    isPlayingRef.current = false;
    engineRef.current?.stop();
    setCurrentTime(0);
    currentTimeRef.current = 0;
  }, []);

  const handleSkipBack = () =>
    handleSeek(Math.max(0, currentTimeRef.current - 5));
  const handleSkipForward = () => handleSeek(currentTimeRef.current + 5);

  const loadSupportedFormats = async () => {
    try {
      const response = await api.getSupportedFormats();
      setSupportedFormats(response.formats || []);
    } catch {
      setSupportedFormats([
        {
          format: "mp3",
          encoding: "MP3",
          validRates: [8000, 16000, 24000],
          defaultRate: 24000,
          contentType: "audio/mpeg",
        },
        {
          format: "wav",
          encoding: "LINEAR16",
          validRates: [8000, 16000, 24000, 32000, 48000],
          defaultRate: 48000,
          contentType: "audio/wav",
        },
      ]);
    }
  };

  const loadProject = async () => {
    const projectId = localStorage.getItem("currentProjectId");
    if (projectId) {
      try {
        const response = await api.getVoiceProject(projectId);
        const sorted = [...(response.project.blocks || [])].sort(
          (a, b) => (a.orderIndex ?? 0) - (b.orderIndex ?? 0),
        );
        let cursor = 0;
        const hydrated = sorted.map((b) => {
          if (b.timelineStart != null) {
            const durSec = b.duration
              ? b.duration > 300
                ? b.duration / 1000
                : b.duration
              : 5;
            cursor = Math.max(cursor, parseFloat(b.timelineStart) + durSec);
            return b;
          }
          const start = cursor;
          const durSec = b.duration
            ? b.duration > 300
              ? b.duration / 1000
              : b.duration
            : 5;
          cursor = start + durSec;
          return { ...b, timelineStart: start };
        });

        const draft = readDraft(response.project.id);
        const mergedBlocks = mergeBlocksWithDraft(hydrated, draft);
        const mergedProject = mergeMusicWithDraft(response.project, draft);

        setProject(mergedProject);
        setBlocks(mergedBlocks);
      } catch {
        await createNewProject();
      }
    } else {
      await createNewProject();
    }
  };

  const createNewProject = async () => {
    const response = await api.createVoiceProject({ name: "Untitled Project" });
    setProject(response.project);
    localStorage.setItem("currentProjectId", response.project.id);
  };

  const handleSwitchProject = async (projectId) => {
    engineRef.current?.stop();
    setIsPlaying(false);
    isPlayingRef.current = false;
    setCurrentTime(0);
    currentTimeRef.current = 0;
    setSelectedBlock(null);
    localStorage.setItem("currentProjectId", projectId);
    try {
      const response = await api.getVoiceProject(projectId);
      const sorted = [...(response.project.blocks || [])].sort(
        (a, b) => (a.orderIndex ?? 0) - (b.orderIndex ?? 0),
      );
      let cursor = 0;
      const hydrated = sorted.map((b) => {
        if (b.timelineStart != null) {
          const durSec = b.duration
            ? b.duration > 300
              ? b.duration / 1000
              : b.duration
            : 5;
          cursor = Math.max(cursor, parseFloat(b.timelineStart) + durSec);
          return b;
        }
        const start = cursor;
        const durSec = b.duration
          ? b.duration > 300
            ? b.duration / 1000
            : b.duration
          : 5;
        cursor = start + durSec;
        return { ...b, timelineStart: start };
      });

      const draft = readDraft(response.project.id);
      const mergedBlocks = mergeBlocksWithDraft(hydrated, draft);
      const mergedProject = mergeMusicWithDraft(response.project, draft);

      setProject(mergedProject);
      setBlocks(mergedBlocks);
      setPlayRendered(false);
    } catch (e) {
      console.error("Failed to switch project:", e);
    }
  };

  const handleAddBlock = async () => {
    if (!project) await createNewProject();
    setShowVoiceSelector(true);
  };

  const handleVoiceSelected = async (voice) => {
    if (!project) return;
    try {
      const schedule = PlaybackEngine.buildSchedule(blocks);
      const defaultStart = schedule.length
        ? schedule[schedule.length - 1].end
        : 0;
      const response = await api.addVoiceBlock(project.id, {
        text: "Enter your text here...",
        voiceName: voice.voiceId,
        voiceDisplayName: voice.displayName,
        languageCode: voice.languageCodes?.[0] ?? "en-GB",
        orderIndex: blocks.length,
        timelineStart: defaultStart,
      });
      setBlocks((prev) => [...prev, response.block]);
      setShowVoiceSelector(false);
      setSelectedBlock(response.block);
    } catch (err) {
      console.error("Failed to add voice block:", err);
    }
  };

  const handleSynthesizeBlock = async (blockId) => {
    setBlocks((prev) =>
      prev.map((b) => (b.id === blockId ? { ...b, status: "processing" } : b)),
    );
    try {
      const response = await api.synthesizeVoiceBlock(project.id, blockId);
      setBlocks((prev) =>
        prev.map((b) => (b.id === blockId ? { ...b, ...response.block } : b)),
      );
      setPlayRendered(false);
    } catch {
      setBlocks((prev) =>
        prev.map((b) => (b.id === blockId ? { ...b, status: "failed" } : b)),
      );
    }
  };

  const handleUpdateBlock = async (blockId, data) => {
    setBlocks((prev) =>
      prev.map((b) => (b.id === blockId ? { ...b, ...data } : b)),
    );
    setPlayRendered(false);
    try {
      await api.updateVoiceBlock(project.id, blockId, data);
    } catch {
      await loadProject();
    }
  };

  const handleDeleteBlock = async (blockId) => {
    setBlocks((prev) => prev.filter((b) => b.id !== blockId));
    if (selectedBlock?.id === blockId) setSelectedBlock(null);
    try {
      await api.deleteVoiceBlock(project.id, blockId);
    } catch {
      await loadProject();
    }
  };

  const patchBlockLayout = useCallback(
    async (blockId, patch) => {
      try {
        await api.patchVoiceBlockLayout(project.id, blockId, patch);
      } catch {
        console.warn("Layout patch failed — continuing with local state");
      }
    },
    [project],
  );

  const handleBlockMove = useCallback(
    (blockId, newStart) => {
      setBlocks((prev) =>
        prev.map((b) =>
          b.id === blockId ? { ...b, timelineStart: newStart } : b,
        ),
      );
      patchBlockLayout(blockId, { timelineStart: newStart });
    },
    [patchBlockLayout],
  );

  const handleBlockResize = useCallback(
    (blockId, newDurationMs) => {
      setBlocks((prev) =>
        prev.map((b) =>
          b.id === blockId ? { ...b, duration: newDurationMs } : b,
        ),
      );
      patchBlockLayout(blockId, { duration: newDurationMs });
    },
    [patchBlockLayout],
  );

  const handleSidebarDragStart = (i) => setDragSrcIndex(i);
  const handleSidebarDragOver = (i) => setDragOverIndex(i);
  const handleSidebarDrop = async (dropIdx) => {
    if (dragSrcIndex === null || dragSrcIndex === dropIdx) {
      setDragSrcIndex(null);
      setDragOverIndex(null);
      return;
    }
    const reordered = [...blocks];
    const [moved] = reordered.splice(dragSrcIndex, 1);
    reordered.splice(dropIdx, 0, moved);
    let cursor = 0;
    const updated = reordered.map((b, i) => {
      const start = cursor;
      const durSec =
        b.duration && b.duration > 0
          ? b.duration > 300
            ? b.duration / 1000
            : b.duration
          : 5;
      cursor += durSec;
      return { ...b, orderIndex: i, timelineStart: start };
    });
    setBlocks(updated);
    setDragSrcIndex(null);
    setDragOverIndex(null);
    updated.forEach((b) =>
      patchBlockLayout(b.id, {
        orderIndex: b.orderIndex,
        timelineStart: b.timelineStart,
      }),
    );
  };

  const handleMusicResize = useCallback(
    async (newDurationSec) => {
      if (!project?.backgroundMusic) return;

      const updated = {
        ...project.backgroundMusic,
        trimStart: project.backgroundMusic.trimStart || 0,
        trimEnd: (project.backgroundMusic.trimStart || 0) + newDurationSec,
        volume: project.backgroundMusic.volume ?? 0.3,
      };

      setProject((prev) => ({ ...prev, backgroundMusic: updated }));
      setPlayRendered(false);

      try {
        await api.updateVoiceProject(project.id, { backgroundMusic: updated });
      } catch (err) {
        console.error("Failed to persist music resize:", err);
      }
    },
    [project],
  );

  const handleMusicSelect = async (track) => {
    if (!project) return;
    try {
      const response = await api.updateVoiceProject(project.id, {
        backgroundMusicId: track.id,
        backgroundMusic: { ...track, trimStart: 0, trimEnd: null, volume: 0.3 },
      });
      setProject(
        response.project || {
          ...project,
          backgroundMusic: {
            ...track,
            trimStart: 0,
            trimEnd: null,
            volume: 0.3,
          },
        },
      );
      setPlayRendered(false);
      setShowMusicLibrary(false);
    } catch (err) {
      console.error("Failed to set background music:", err);
    }
  };

  const handleTrimApply = async ({ trimStart, trimEnd }) => {
    if (!project?.backgroundMusic) return;
    const updated = {
      ...project.backgroundMusic,
      trimStart,
      trimEnd,
      volume: project.backgroundMusic.volume ?? 0.3,
    };
    setProject((prev) => ({ ...prev, backgroundMusic: updated }));
    setShowTrimModal(false);
    setPlayRendered(false);
    try {
      await api.updateVoiceProject(project.id, { backgroundMusic: updated });
    } catch (err) {
      console.error("Failed to save trim:", err);
    }
  };

  const handleUpdateProjectSettings = async ({ format, sampleRate }) => {
    try {
      const response = await api.updateVoiceProject(project.id, {
        outputFormat: format,
        sampleRate,
      });
      setProject(
        response.project || { ...project, outputFormat: format, sampleRate },
      );
      setShowProjectSettings(false);
    } catch (err) {
      console.error("Failed to update project settings:", err);
    }
  };

  const handleRender = async () => {
    if (!project || isRendering) return;
    setIsRendering(true);
    setRenderError(null);
    try {
      const response = await api.renderVoiceProject(project.id);
      setProject((prev) => ({ ...prev, ...response.project }));
      setPlayRendered(true);
    } catch (err) {
      setRenderError(
        err?.response?.data?.message || "Render failed. Please try again.",
      );
    } finally {
      setIsRendering(false);
    }
  };

  const pendingCount = blocks.filter(
    (b) => b.status !== "completed" && b.status !== "processing",
  ).length;
  const progress =
    totalDuration > 0 ? Math.min((currentTime / totalDuration) * 100, 100) : 0;

  return (
    <div
      className="flex flex-col h-screen text-white overflow-hidden"
      style={{ background: "#070a10" }}
    >
      {/* ── Top Bar ── */}
      <div
        className="flex items-center justify-between px-4 py-2 flex-shrink-0"
        style={{
          borderBottom: "1px solid rgba(255,255,255,0.06)",
          background: "rgba(7,10,16,0.9)",
          backdropFilter: "blur(12px)",
        }}
      >
        {/* Left */}
        <div className="flex items-center gap-2 min-w-0">
          <button
            onClick={() => setSidebarCollapsed((c) => !c)}
            className="w-8 h-8 flex items-center justify-center rounded-xl text-gray-700 hover:text-white transition-colors"
            style={{
              background: sidebarCollapsed
                ? "rgba(255,255,255,0.05)"
                : undefined,
            }}
          >
            <Layers size={15} />
          </button>

          <button
            onClick={() => setShowProjectsDrawer(true)}
            className="flex items-center gap-2 px-3 py-1.5 rounded-xl transition-all group"
            style={{
              background: "rgba(255,255,255,0.03)",
              border: "1px solid rgba(255,255,255,0.07)",
            }}
          >
            <span className="text-sm font-semibold text-gray-300 group-hover:text-white truncate max-w-[160px] transition-colors">
              {project?.name || "Loading…"}
            </span>
            <FolderOpen
              size={12}
              className="text-gray-700 group-hover:text-violet-400 transition-colors flex-shrink-0"
            />
          </button>

          <div className="flex items-center gap-1.5 hidden sm:flex">
            <div
              className="w-1.5 h-1.5 rounded-full"
              style={{
                background: completedBlocks.length > 0 ? "#34d399" : "#374151",
              }}
            />
            <span className="text-[11px] text-gray-700 font-mono">
              {completedBlocks.length}/{blocks.length} ready
            </span>
          </div>
        </div>

        {/* Centre – transport */}
        <div className="flex items-center gap-1">
          <IconBtn onClick={handleSkipBack} title="Skip back 5s">
            <SkipBack size={15} />
          </IconBtn>
          <IconBtn onClick={handleStop} title="Stop">
            <Square size={13} />
          </IconBtn>
          <IconBtn
            onClick={togglePlay}
            disabled={!canPlay}
            variant="solid"
            title={
              isPlaying
                ? "Pause"
                : blocks.some((b) => b.status === "processing")
                  ? "Synthesizing…"
                  : "Play"
            }
          >
            {isPlaying ? (
              <Pause size={16} />
            ) : blocks.some((b) => b.status === "processing") ? (
              <Loader size={15} className="animate-spin" />
            ) : (
              <Play size={16} className="ml-0.5" />
            )}
          </IconBtn>
          <IconBtn onClick={handleSkipForward} title="Skip forward 5s">
            <SkipForward size={15} />
          </IconBtn>

          {/* Time display */}
          <div
            className="mx-2 px-3 py-1 rounded-lg font-mono text-xs tabular-nums"
            style={{ background: "rgba(255,255,255,0.04)", color: "#9ca3af" }}
          >
            {formatTime(currentTime)}
            <span style={{ color: "rgba(255,255,255,0.2)" }}> / </span>
            {formatTime(totalDuration)}
          </div>

          <IconBtn
            onClick={() => setIsLooping((l) => !l)}
            active={isLooping}
            title="Loop"
          >
            <Repeat size={14} />
          </IconBtn>
        </div>

        {/* Right */}
        <div className="flex items-center gap-1.5">
          {/* Volume */}
          <div className="flex items-center gap-1.5 mr-1">
            <button
              onClick={() => setIsMuted((m) => !m)}
              className="text-gray-600 hover:text-white transition-colors p-1"
            >
              {isMuted ? <VolumeX size={14} /> : <Volume2 size={14} />}
            </button>
            <div
              className="relative w-16 h-1 rounded-full hidden sm:block"
              style={{ background: "rgba(255,255,255,0.08)" }}
            >
              <div
                className="absolute left-0 top-0 h-full rounded-full"
                style={{
                  width: `${(isMuted ? 0 : masterVolume) * 100}%`,
                  background: "linear-gradient(90deg, #7c3aed, #a855f7)",
                }}
              />
              <input
                type="range"
                min={0}
                max={1}
                step={0.05}
                value={isMuted ? 0 : masterVolume}
                onChange={(e) => {
                  const v = parseFloat(e.target.value);
                  setMasterVolume(v);
                  if (v > 0) setIsMuted(false);
                }}
                className="absolute inset-0 w-full opacity-0 cursor-pointer"
              />
            </div>
          </div>

          {/* Music */}
          <IconBtn
            onClick={() => setShowMusicLibrary(true)}
            active={!!project?.backgroundMusicId}
            title="Background Music"
          >
            <Music size={14} />
          </IconBtn>

          {/* Settings */}
          <IconBtn
            onClick={() => setShowProjectSettings(true)}
            title="Project Settings"
          >
            <Settings size={14} />
          </IconBtn>

          {/* Separator */}
          <div
            className="w-px h-5 mx-1"
            style={{ background: "rgba(255,255,255,0.07)" }}
          />

          {/* Render */}
          <button
            onClick={handleRender}
            disabled={isRendering || completedBlocks.length === 0}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold text-white transition-all disabled:opacity-40 disabled:cursor-not-allowed"
            style={{
              background: "linear-gradient(135deg, #7c3aed, #6d28d9)",
              boxShadow: "0 2px 12px rgba(124,58,237,0.25)",
            }}
          >
            {isRendering ? (
              <>
                <Loader size={12} className="animate-spin" />
                <span>Rendering…</span>
              </>
            ) : (
              <>
                <Zap size={12} />
                <span>Render</span>
              </>
            )}
          </button>

          {/* Export */}
          {project?.audioUrl && (
            <a
              href={project.audioUrl}
              download
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-medium text-gray-400 hover:text-white transition-all"
              style={{
                background: "rgba(255,255,255,0.04)",
                border: "1px solid rgba(255,255,255,0.07)",
              }}
            >
              <Download size={12} />
              <span>Export</span>
            </a>
          )}

          {/* Collapse editor */}
          <button
            onClick={() => setEditorCollapsed((e) => !e)}
            className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-700 hover:text-white transition-colors ml-1"
          >
            <ChevronRight
              size={15}
              className={`transition-transform ${editorCollapsed ? "rotate-180" : ""}`}
            />
          </button>
        </div>
      </div>

      {/* ── Error bar ── */}
      {renderError && (
        <div
          className="flex items-center gap-2 px-4 py-2 text-sm text-red-400"
          style={{
            background: "rgba(239,68,68,0.08)",
            borderBottom: "1px solid rgba(239,68,68,0.15)",
          }}
        >
          <AlertCircle size={14} />
          <span>{renderError}</span>
          <button
            onClick={() => setRenderError(null)}
            className="ml-auto text-red-600 hover:text-red-400"
          >
            ×
          </button>
        </div>
      )}

      {/* ── Progress scrub bar ── */}
      <div
        className="relative h-[3px] cursor-pointer flex-shrink-0"
        style={{ background: "rgba(255,255,255,0.04)" }}
        onClick={(e) => {
          const rect = e.currentTarget.getBoundingClientRect();
          handleSeek(((e.clientX - rect.left) / rect.width) * totalDuration);
        }}
      >
        <div
          className="h-full transition-none"
          style={{
            width: `${progress}%`,
            background: "linear-gradient(90deg, #7c3aed, #a855f7)",
          }}
        />
        {/* Playhead dot */}
        <div
          className="absolute top-1/2 -translate-y-1/2 w-2.5 h-2.5 rounded-full shadow-lg pointer-events-none"
          style={{
            left: `${progress}%`,
            background: "#a855f7",
            boxShadow: "0 0 8px rgba(168,85,247,0.8)",
            transform: "translate(-50%, -50%)",
          }}
        />
      </div>

      {/* ── Main area ── */}
      <div className="flex flex-1 overflow-hidden">
        {/* ── Sidebar ── */}
        <div
          className={`flex flex-col flex-shrink-0 transition-all duration-200 ${sidebarCollapsed ? "w-0 overflow-hidden" : "w-72"}`}
          style={{
            borderRight: "1px solid rgba(255,255,255,0.05)",
            background: "rgba(0,0,0,0.3)",
          }}
        >
          {/* Add block button */}
          <div
            className="p-3"
            style={{ borderBottom: "1px solid rgba(255,255,255,0.05)" }}
          >
            <button
              onClick={handleAddBlock}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold text-white transition-all group"
              style={{
                background:
                  "linear-gradient(135deg, rgba(124,58,237,0.9), rgba(109,40,217,0.9))",
                boxShadow: "0 4px 16px rgba(124,58,237,0.2)",
                border: "1px solid rgba(139,92,246,0.3)",
              }}
            >
              <Plus
                size={15}
                className="group-hover:rotate-90 transition-transform duration-200"
              />
              <span>Add Voice Block</span>
            </button>
          </div>

          {/* Stats row */}
          <div
            className="flex items-center gap-0 px-0"
            style={{ borderBottom: "1px solid rgba(255,255,255,0.05)" }}
          >
            {[
              { label: "blocks", value: blocks.length, color: "#9ca3af" },
              {
                label: "ready",
                value: completedBlocks.length,
                color: "#34d399",
              },
              {
                label: "pending",
                value: pendingCount,
                color: pendingCount > 0 ? "#fbbf24" : "#4b5563",
              },
            ].map((stat, i) => (
              <React.Fragment key={stat.label}>
                <div className="flex-1 flex flex-col items-center py-2.5">
                  <span
                    className="text-sm font-bold"
                    style={{ color: stat.color }}
                  >
                    {stat.value}
                  </span>
                  <span className="text-[9px] text-gray-700 uppercase tracking-widest mt-0.5">
                    {stat.label}
                  </span>
                </div>
                {i < 2 && (
                  <div
                    className="w-px h-6"
                    style={{ background: "rgba(255,255,255,0.05)" }}
                  />
                )}
              </React.Fragment>
            ))}
          </div>

          {/* Block list */}
          <div
            className="flex-1 overflow-y-auto p-3 space-y-2"
            style={{
              scrollbarWidth: "thin",
              scrollbarColor: "rgba(139,92,246,0.2) transparent",
            }}
          >
            {blocks.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full gap-3 pb-8">
                <div
                  className="w-14 h-14 rounded-2xl flex items-center justify-center"
                  style={{
                    background: "rgba(255,255,255,0.03)",
                    border: "1px solid rgba(255,255,255,0.07)",
                  }}
                >
                  <Mic2 size={22} className="text-gray-700" />
                </div>
                <div className="text-center">
                  <p className="text-xs font-medium text-gray-600">
                    No blocks yet
                  </p>
                  <p className="text-[11px] text-gray-700 mt-0.5">
                    Add a voice block to start
                  </p>
                </div>
              </div>
            ) : (
              blocks.map((block, index) => (
                <SidebarBlock
                  key={block.id}
                  block={block}
                  index={index}
                  isSelected={selectedBlock?.id === block.id}
                  onSelect={() => {
                    const next = selectedBlock?.id === block.id ? null : block;
                    setSelectedBlock(next);
                    if (next) setEditorCollapsed(false);
                  }}
                  onSynthesize={() => handleSynthesizeBlock(block.id)}
                  onUpdate={(data) => handleUpdateBlock(block.id, data)}
                  onDelete={() => handleDeleteBlock(block.id)}
                  onDragStart={handleSidebarDragStart}
                  onDragOver={handleSidebarDragOver}
                  onDrop={handleSidebarDrop}
                  isDraggingOver={
                    dragOverIndex === index && dragSrcIndex !== index
                  }
                  avatarImageUrl={avatarMap[block.voiceName] ?? null}
                />
              ))
            )}
          </div>
        </div>

        {/* ── Timeline ── */}
        <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
          <div className="flex-1 p-4 overflow-hidden">
            <Timeline
              blocks={blocks}
              backgroundMusic={project?.backgroundMusic || null}
              currentTime={currentTime}
              isPlaying={isPlaying}
              onSeek={handleSeek}
              onBlockMove={handleBlockMove}
              onBlockResize={handleBlockResize}
              onMusicResize={handleMusicResize}
              onTrimClick={
                project?.backgroundMusic
                  ? () => setShowTrimModal(true)
                  : undefined
              }
              onBlockSelect={(id) => {
                setSelectedBlock(blocks.find((b) => b.id === id) || null);
                if (id) setEditorCollapsed(false);
              }}
              selectedBlockId={selectedBlock?.id}
            />
          </div>

          {/* Keyboard hints */}
          <div
            className="flex-shrink-0 px-4 pb-2 flex items-center gap-3 flex-wrap"
            style={{ borderTop: "1px solid rgba(255,255,255,0.04)" }}
          >
            {[
              ["Space", "Play"],
              ["R", "Restart"],
              ["← →", "Seek"],
              ["M", "Mute"],
              ["L", "Loop"],
              ["Del", "Delete"],
            ].map(([key, label]) => (
              <div key={key} className="flex items-center gap-1">
                <kbd
                  className="text-[9px] font-mono px-1.5 py-0.5 rounded-md"
                  style={{
                    background: "rgba(255,255,255,0.05)",
                    border: "1px solid rgba(255,255,255,0.08)",
                    color: "#6b7280",
                  }}
                >
                  {key}
                </kbd>
                <span className="text-[9px] text-gray-700">{label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* ── Block Editor ── */}
        <div
          className={`flex flex-col flex-shrink-0 transition-all duration-200 ${editorCollapsed ? "w-0 overflow-hidden" : "w-80"}`}
          style={{
            borderLeft: "1px solid rgba(255,255,255,0.05)",
            background: "rgba(0,0,0,0.3)",
          }}
        >
          <div
            className="px-4 py-2.5 flex items-center justify-between"
            style={{ borderBottom: "1px solid rgba(255,255,255,0.05)" }}
          >
            <span className="text-[10px] font-bold text-gray-700 uppercase tracking-[0.12em]">
              Block Editor
            </span>
            {selectedBlock && (
              <span className="text-[10px] text-gray-700 truncate ml-2 max-w-[120px]">
                {selectedBlock.voiceDisplayName || selectedBlock.voiceName}
              </span>
            )}
          </div>
          <div className="flex-1 overflow-y-auto">
            <BlockEditor
              block={selectedBlock}
              blockIndex={
                selectedBlock
                  ? blocks.findIndex((b) => b.id === selectedBlock.id)
                  : 0
              }
              avatarImageUrl={
                selectedBlock
                  ? (avatarMap[selectedBlock.voiceName] ?? null)
                  : null
              }
              onUpdate={(data) => {
                if (selectedBlock) handleUpdateBlock(selectedBlock.id, data);
              }}
              onSynthesize={() => {
                if (selectedBlock) handleSynthesizeBlock(selectedBlock.id);
              }}
            />
          </div>
        </div>
      </div>

      {/* ── Modals ── */}
      {showVoiceSelector && (
        <VoiceSelector
          onSelect={handleVoiceSelected}
          onClose={() => setShowVoiceSelector(false)}
        />
      )}
      {showProjectsDrawer && (
        <ProjectsDrawer
          currentProjectId={project?.id}
          onSwitch={handleSwitchProject}
          onClose={() => setShowProjectsDrawer(false)}
        />
      )}
      {showMusicLibrary && (
        <MusicLibrary
          onSelect={handleMusicSelect}
          onClose={() => setShowMusicLibrary(false)}
        />
      )}
      {showTrimModal && project?.backgroundMusic && (
        <MusicTrimModal
          track={project.backgroundMusic}
          initialTrimStart={project.backgroundMusic.trimStart ?? 0}
          initialTrimEnd={
            project.backgroundMusic.trimEnd ??
            project.backgroundMusic.duration ??
            null
          }
          onApply={handleTrimApply}
          onClose={() => setShowTrimModal(false)}
        />
      )}
      {showProjectSettings && (
        <ProjectSettings
          project={project}
          supportedFormats={supportedFormats}
          onSave={handleUpdateProjectSettings}
          onClose={() => setShowProjectSettings(false)}
        />
      )}
    </div>
  );
};

const formatTime = (seconds) => {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  const ds = Math.floor((seconds % 1) * 10);
  return `${m}:${s.toString().padStart(2, "0")}.${ds}`;
};

export default RecordingStudio;
